import * as React from 'react';
import { UploadProps, UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import useDefaultUploaderProps from './useDefaultUploaderProps';
import Api from '../services/Api';
import type ImageWebsiteLogo from '../steps/Design/view/files/ImageWebsiteLogo';
import DesignModel from '../steps/Design/models/DesignModel';
import UploadedWebsiteLogo from '../steps/Design/view/files/UploadedWebsiteLogo';
import BusinessModel from '../steps/FindYourBusiness/models/BusinessModel';


interface UseLogoUploadReturn {
  isUploading: boolean;
  uploadProps: UploadProps;
}

interface UseLogoUploadOptions {
  onSuccess?: (logo: ImageWebsiteLogo) => void;
  onError?: (error: any) => void;
}

const useLogoUpload = (options: UseLogoUploadOptions = {}): UseLogoUploadReturn => {
  const [isUploading, setIsUploading] = React.useState(false);
  const defaultUploadProps = useDefaultUploaderProps();

  const handleChange: UploadProps['onChange'] = React.useCallback(
    (info: UploadChangeParam<UploadFile<any>>) => {
      if (info.file.status === 'uploading') {
        setIsUploading(true);
      } else {
        setIsUploading(false);
      }

      if (info.file.status === 'done') {
        // Create a new logo from the uploaded file
        const newUploadedLogo = new UploadedWebsiteLogo(
          info.file,
          (info.file as any).__data__.data.data.colors,
          {
            width: (info.file as any).__data__.data.data.width,
            height: (info.file as any).__data__.data.data.height
          }
        );
        
        // Append the new logo to the existing uploaded logos
        DesignModel.setUploadedLogs([
          ...(DesignModel.uploadedLogos || []),
          newUploadedLogo
        ]);

        options.onSuccess?.(newUploadedLogo);
      }

      if (info.file.status === 'error') {
        options.onError?.(info.file.error);
      }
    },
    [options]
  );

  const uploadProps: UploadProps = {
    name: 'file',
    ...defaultUploadProps,
    customRequest: (requestOptions) => {
      const { onSuccess, onError, file } = requestOptions;

      const formData = new FormData();
      formData.append('logo', file);
      formData.append('category', BusinessModel.getWizardData().category);

      Api.uploadLogo(formData)
        .then((result) => {
          (file as any).__data__ = result;
          onSuccess && onSuccess(file);
        })
        .catch((error) => {
          console.error(error);
          onError && onError(error);
        });
    },
    showUploadList: false,
    onChange: handleChange
  };

  return {
    isUploading,
    uploadProps
  };
};

export default useLogoUpload;